import { BaseDataService } from './BaseDataService';
import { User, Team, Organization, PagedData, SurveyNote } from '../../models';
import { Auth, NotificationService } from '../../services';
import { HasUploadImage } from '../../interfaces';
import { FRONTEND_ROOT } from '../../config/host-config';

export class UserDataService extends BaseDataService<User> implements HasUploadImage {
    constructor(notificationService: NotificationService) {
        super('User', 'Users', notificationService);
    }

    public getByOrganization(organizationId: number): Promise<User[]> {
        const endpoint = `${this.endpointRoot}/byOrganization/${organizationId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getByOrganizationLight(organizationId: number): Promise<User[]> {
        const endpoint = `${this.endpointRoot}/byOrganizationLight/${organizationId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getByOrganizationLightCandidates(organizationId: number): Promise<User[]> {
        const endpoint = `${this.endpointRoot}/byOrganizationLightCandidates/${organizationId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getByOrganizationLightFormer(organizationId: number): Promise<User[]> {
        const endpoint = `${this.endpointRoot}/byOrganizationLightFormer/${organizationId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getByOrganizationPaged(organization: Organization, page: number, pageSize: number, filter?: string, sortProperty?: string, sortDirection?: string): Promise<PagedData<User>> {
        let endpoint = `${this.endpointRoot}/byOrganizationPaged/${organization.id}?page=${page}&pageSize=${pageSize}`;
        const init = this.buildRequestInit('GET');

        if (filter !== undefined) {
            endpoint = `${endpoint}&filter=${filter}`;
        }

        if (sortProperty !== undefined) {
            endpoint = `${endpoint}&sortProperty=${sortProperty}`;

            if (sortDirection !== undefined) {
                endpoint = `${endpoint}&sortDirection=${sortDirection}`;
            } else {
                endpoint = `${endpoint}&sortDirection=asc`;
            }
        }

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getPagedLight(page: number, pageSize: number, filter?: string, sortProperty?: string, sortDirection?: string): Promise<PagedData<User>> {
        let endpoint = `${this.endpointRoot}/pagedLight?page=${page}&pageSize=${pageSize}`;
        const init = this.buildRequestInit('GET');

        if (filter !== undefined) {
            endpoint = `${endpoint}&filter=${filter}`;
        }

        if (sortProperty !== undefined) {
            endpoint = `${endpoint}&sortProperty=${sortProperty}`;

            if (sortDirection !== undefined) {
                endpoint = `${endpoint}&sortDirection=${sortDirection}`;
            } else {
                endpoint = `${endpoint}&sortDirection=asc`;
            }
        }

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getByTeam(team: Team): Promise<User[]> {
        const endpoint = `${this.endpointRoot}/byTeam/${team.id}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getByTeamLight(team: Team): Promise<User[]> {
        const endpoint = `${this.endpointRoot}/byTeamLight/${team.id}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public sendPasswordReset(id: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${id}/sendPasswordReset`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return;
            }
            throw new Error('Network response was not ok.');
        });
    }

    public async bypassMfa(id: number): Promise<any> {
        try {
            const auth = new Auth();
            await auth.bypassMfa(id);
        } catch(e) {
            this.notificationService.addError(e);
        }
    }

    public createPersonalAccount(id: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${id}/createLogin`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return;
            }
            throw new Error('Network response was not ok.');
        });
    }

    public deactivate(id: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${id}/deactivate`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addNotification(`${this.entityType} deactivated successfully.`);
                return;
            }

            const message = `Error deactivating ${this.entityType.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public reactivate(id: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${id}/activate`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addNotification(`${this.entityType} reactivated successfully.`);
                return;
            }

            const message = `Error reactivating ${this.entityType.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public checkIfEmailIsInUse(email: string, id?: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/checkEmail`;
        const init = this.buildRequestInit('POST', { email: email, id: id });

        return fetch(endpoint, init);
    }

    uploadImage(parentId: number, file: File) {
        const endpoint = `${this.endpointRoot}/${parentId}/uploadAvatar`;
        const init = this.buildRequestInit('POST');

        const data = new FormData();
        data.append('imageFile', file);

        init.body = data;

        return fetch(endpoint, init).then(
            (response) => {
                this.notificationService.addNotification(`Avatar uploaded successfully.`);
                return response.json();
            },
            () => {
                this.notificationService.addError(`Error uploading avatar. Please try again.`);
            }
        );
    }

    updateUserSettings(user: User) {
        const endpoint = `${this.endpointRoot}/updateUserSettings`;
        const init = this.buildRequestInit('POST', user);

        return fetch(endpoint, init).then(
            (response) => {
                this.notificationService.addNotification(`User settings updated successfully.`);
                return response.json();
            },
            () => {
                this.notificationService.addError(`Error updating user settings. Please try again.`);
            }
        );
    }

    public getNotes(userId: number): Promise<SurveyNote[]> {
        const endpoint = `${this.endpointRoot}/${userId}/surveyNotes`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public createNote(id: number, note: SurveyNote): Promise<SurveyNote> {
        const endpoint = `${this.endpointRoot}/${id}/surveyNotes`;

        const init = this.buildRequestInit('POST', note);

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public deleteNote(noteId: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/surveyNotes/${noteId}`;

        const init = this.buildRequestInit('DELETE');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return;
            }
            throw new Error('Network response was not ok.');
        });
    }

    async startImpersonation(id: number): Promise<any> {
        try {
            const auth = new Auth();
            await auth.impersonateUser(id);
        } catch(e) {
            this.notificationService.addError(e);
        }
    }

    async stopImpersonation(): Promise<any> {
        try {
            const auth = new Auth();
            await auth.stopImpersonating();
        } catch(e) {
            this.notificationService.addError(e);
        }
    }
}


// WEBPACK FOOTER //
// ./src/services/api/UserData.tsx